import { useState } from "react";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { buildFaqPageSchema } from "@/components/feature/FaqAccordion";
import { usePublicFaqs } from "@/hooks/usePublicContent";

const fallbackFaqs = [
  {
    q: "Do I need to book in advance?",
    a: "We recommend booking at least 3–5 days ahead for families and 2–3 weeks for schools and corporate groups. Winter (Oct–Feb) fills fastest, so earlier is better.",
  },
  {
    q: "Can you customise an experience for us?",
    a: "Absolutely. Most of our itineraries are tailored to your group, interests, season and time. Just tell us what you're hoping for and we'll shape a plan around it.",
  },
  {
    q: "How do I get to Konaseema?",
    a: "The nearest airport is Rajahmundry (about 1.5–2 hours by road) and the closest railheads are Amalapuram and Rajahmundry. We'll share a clear route and map once your dates are confirmed.",
  },
  {
    q: "Is it suitable for young children or large groups?",
    a: "Yes. We host families with small children, school groups up to 120 students and corporate teams up to 150 people, splitting larger groups into rotating batches.",
  },
  {
    q: "What languages are the experiences in?",
    a: "Experiences are hosted in Telugu, English and Hindi. Do let us know your preferred language so we can match you with the right host.",
  },
];

export default function ContactFaq() {
  const [open, setOpen] = useState<number | null>(0);
  const faqs = usePublicFaqs("General", fallbackFaqs);

  return (
    <section className="sun-glaze bg-background-50 py-16 md:py-24">
      {faqs.length > 0 ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(buildFaqPageSchema(faqs)) }}
        />
      ) : null}
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
          <Reveal className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
              <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
                Good to Know
              </span>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight text-foreground-950 font-semibold">
              Questions We Hear Most
            </h2>
            <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
              Still curious about something? Send us a message using the form above and we'll answer
              personally.
            </p>
            <a
              href="#contact-form"
              className="mt-7 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Ask a Question
              <i className="ri-arrow-down-line"></i>
            </a>
          </Reveal>

          <Reveal className="lg:col-span-7" delay={120}>
            <div className="space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = open === index;
                return (
                  <div
                    key={faq.q}
                    className={`bg-background-100 rounded-lg border transition-colors ${
                      isOpen ? "border-accent-300" : "border-background-200"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? null : index)}
                      className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-heading text-lg text-foreground-950 font-semibold">
                        {faq.q}
                      </span>
                      <span
                        className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 transition-colors ${
                          isOpen
                            ? "bg-accent-500 text-background-50"
                            : "bg-background-200 text-foreground-700"
                        }`}
                      >
                        <i className={`${isOpen ? "ri-subtract-line" : "ri-add-line"} text-lg`}></i>
                      </span>
                    </button>
                    {isOpen && (
                      <p className="px-6 pb-6 text-sm md:text-base text-foreground-600 leading-relaxed">
                        {faq.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}